import { reactular } from '@vendhq/reactular'
import {
  Header,
  Icon,
  PageHeader,
  Section,
  Text,
} from '@lightspeed/design-system-react'
import React from 'react'
import { T, useT } from '@transifex/react'
import { NoPermissionsPage } from '@vend/react-utilities'
import {
  useSaleSyncManager,
  useUserManager,
  useVdFeatureManager,
  useVdTracking,
  useWebRegisterStatusManager,
} from '../../ng-compat/hooks'
import { drop } from '../../factories/database/database'
import { useAppSelector } from '../../state/hooks'
import {
  selectActiveRegisterId,
  selectLoggedInUserId,
} from '../../state/slices/posState/posStateSlice'
import {
  FEATURE_ENABLE_REGISTER_ACCESS_CONTROL,
  PERMISSIONS,
} from '../../constants'
import { SaleModals } from '../sale/SaleModals'
import { useErroredSales } from '../sale/useErroredSales'
import { ErrorSales } from './ErrorSales'
import { ResetData } from './ResetData'

export const StatusView: React.FC = () => {
  const t = useT()
  const {
    erroredSales,
    erroredSalesFileUrl,
    outletTimeZone,
  } = useErroredSales()
  const saleSyncManager = useSaleSyncManager()
  const userManager = useUserManager()
  const vdFeatureManager = useVdFeatureManager()
  const webRegisterStatusManager = useWebRegisterStatusManager()
  const vdTracking = useVdTracking()
  const registerId = useAppSelector(selectActiveRegisterId)
  // Trigger a re-render when the user changes
  useAppSelector(selectLoggedInUserId)

  const hasAccessToReset = () => {
    const registerAccessControlEnabled = vdFeatureManager.get(
      FEATURE_ENABLE_REGISTER_ACCESS_CONTROL
    )
    return registerAccessControlEnabled
      ? userManager.hasPermission(PERMISSIONS.REGISTER_STATUS_RESET)
      : true
  }

  const resetData = () => {
    vdTracking.trackUserEvent({
      eventName: 'Reset Data',
      metadata: {
        register_id: registerId,
      },
      useBatching: false,
      useSendBeacon: true,
    })

    drop()
      // Refresh the application cache and reload the browser. This effectively triggers a cold load and
      // sync as if the user is accessing Web Register for the first time in this browser
      .then(() => {
        webRegisterStatusManager.refreshCacheAndReload()
      })
  }

  return (
    <>
      <SaleModals />
      <PageHeader>{t('Status')}</PageHeader>
      {erroredSales.length > 0 && (
        <Section>
          <Header type="sub-heading" level="4">
            <Icon icon="warning" className="vd-text--negative vd-mr1" />
            <T
              _str={
                '{salesCount, plural,' +
                'one {Unable to sync {salesCount} sale to the server}' +
                'other {Unable to sync {salesCount} sales to the server}}'
              }
              salesCount={erroredSales.length}
            />
          </Header>
          <ErrorSales
            sales={erroredSales}
            syncInProgress={saleSyncManager.saleSyncRetryInProgress}
            fileUrl={erroredSalesFileUrl}
            timeZone={outletTimeZone}
          />
        </Section>
      )}
      <Section>
        <Header level="4" type="sub-heading">
          {t('Reset local data')}
        </Header>
        <Text tag="p" className="vd-mb5">
          {t(
            "We keep a copy of some of your store data in your web browser so you can keep selling if you lose your Internet connection. Sometimes, this gets out of sync. Resetting it can help if you're having trouble with Lightspeed Retail."
          )}
        </Text>
        {hasAccessToReset() ? (
          <ResetData
            onConfirm={resetData}
            disabled={saleSyncManager.saleSyncRetryInProgress}
          />
        ) : (
          <NoPermissionsPage
            copy={t(
              'You don’t have permission to access the register status page. Switch to a user with permission.'
            )}
          />
        )}
      </Section>
    </>
  )
}

export const StatusViewComponent = reactular(StatusView, [], 'reactWrapper')
